import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Grow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  styled,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Tooltip from "@mui/material/Tooltip";
import colors from "colors/color";
import cancel_icon from "../../assets/img/cancel_icon.svg";
import { useTranslation } from "react-i18next";
import ApplicationTable from "./ApplicationTable";
import ApplicationTable2 from "./ApplicationsTable2";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { styled as muiStyled } from "@mui/material/styles";
import CustomPagination from "views/client/pagination";

const StyledSelect = muiStyled(Select)(({ theme }) => ({
  minWidth: 120,
  marginRight: theme.spacing(2),
}));

const StyledTypography = muiStyled(Typography)(() => ({
  color: "#000",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
}));
const StyledTableHead = styled(TableHead)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  "& .MuiTableRow-root": {
    height: "50px",
    margin: "50px 0 0 0",
    "& .MuiTableCell-root": {
      fontFamily: "Poppins",
      backgroundColor: "#D9D9D933",
      fontWeight: 500,
      color: "#00000080",
      borderBottom: "none",
      fontSize: "11px",
    },
    "& .MuiTableCell-root:first-of-type": {
      borderRadius: "10px",
    },
    "& .MuiTableCell-root:last-of-type": {
      borderRadius: "10px",
    },
  },
}));
const CancelButton = styled(Button)(({ theme }) => ({
  paddingTop: "14px",
  paddingRight: "24px",
  paddingBottom: "14px",
  paddingLeft: "24px",

  border: "1px solid #FF0000",
  bgcolor: "#fff",
  color: "#FF0000",
  borderRadius: "10px",
  textTransform: "none",
  fontFamily: "Poppins",
  fontWeight: 400,
  fontSize: "12px",
  minWidth: "120px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "&:hover": {
    bgcolor: "#FF0000",
    borderColor: "#FF0000",
    "& .MuiTypography-root": {
      color: "#FFFFFF",
    },
    "& img": {
      filter: "brightness(0) invert(1)",
    },
  },
  // "&.Mui-disabled": {
  //   bgcolor: "#FF0000",
  //   color: "#fff",
  //   borderColor: "white",
  //   "& .MuiTypography-root": {
  //     color: "#FFFFFF",
  //   },
  //   "& img": {
  //     filter: "brightness(0) invert(1)",
  //   },
  // },
  "& .MuiTypography-root": {
    color: "#FF0000",
    fontFamily: "Poppins",
    fontWeight: 400,
    fontSize: "12px",
  },
}));

const ScrapedUsersViewer = ({ recruiterId, apiUrl, refreshTrigger, selectedCategoryFilter, onCategoryFilterChange }) => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewType, setViewType] = useState("card");
  const [sourceFilter, setSourceFilter] = useState("");
  // Remove categoryFilter state since it's now controlled by parent
  // const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const { t } = useTranslation();

  const fetchUsers = async (selectedCategory = null) => {
    setLoading(true);
    try {
      // Use different API endpoints based on whether category filtering is needed
      let url;
      if (selectedCategory) {
        // Use the new category-specific API endpoint
        url = `${apiUrl}candidates/api/unified-candidates/search-by-category/`;
      } else {
        // Use the general search API endpoint
        url = `${apiUrl}candidates/api/unified-candidates/search/`;
      }
      
      const params = new URLSearchParams();

      // Always include recruiter_id
      params.append("recruiter_id", recruiterId);

      // Filter for scraped_users only
      params.append("source_filters", JSON.stringify({
        regular: false,
        scraped: false,
        scraped_users: true
      }));

      if (sourceFilter) {
        params.append("source", sourceFilter);
      }
      
      // Add category filter if using category-specific endpoint
      if (selectedCategory) {
        params.append("category_id", selectedCategory);
        console.log("🔍 Frontend DEBUG - Using category API with category_id:", selectedCategory);
      }

      // Request all records for frontend pagination
      params.append("page_size", "1000"); // Request up to 1000 records

      // Add the query parameters to the URL if they exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log("🔍 Frontend DEBUG - Fetching users with URL:", url);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      console.log("🔍 Frontend DEBUG - Received data:", data);

      // Handle search unified candidates response format
      let usersData = [];
      if (data.results) {
        // Search unified candidates paginated response
        usersData = data.results;
        console.log(
          `🔍 Frontend DEBUG - Loaded ${usersData.length} users from API response. Total: ${
            data.count || "unknown"
          }`
        );
      } else if (Array.isArray(data)) {
        // Direct array response
        usersData = data;
        console.log(
          `🔍 Frontend DEBUG - Loaded ${usersData.length} users from direct array response`
        );
      } else {
        console.warn("🔍 Frontend WARNING - Unexpected data format:", data);
        usersData = [];
      }

      setUsers(usersData);
      setError(null);
      
      // Debug: Log the first user to check category data
      if (usersData.length > 0) {
        console.log("🔍 Frontend DEBUG - First user data:", usersData[0]);
        console.log("🔍 Frontend DEBUG - First user category:", usersData[0].category);
        console.log("🔍 Frontend DEBUG - First user source:", usersData[0].source);
      } else {
        console.log("🔍 Frontend DEBUG - No users found with current filters");
      }
    } catch (err) {
      setError("Failed to load scraped users");
      console.error("🔍 Frontend ERROR - fetchUsers failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(selectedCategoryFilter);
  }, [sourceFilter, selectedCategoryFilter, refreshTrigger]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        // `${apiUrl}authentication/categories-with-subcategories/`,
        `${apiUrl}authentication/categories/recruiter/${recruiterId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      setCategories(data.categories);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(
        `${apiUrl}recruiters/${recruiterId}/scraped-users/${userId}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete user");
      fetchUsers(selectedCategoryFilter);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting user:", err);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const response = await fetch(
        `${apiUrl}recruiters/${recruiterId}/scraped-users/delete-all/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete all users");
      fetchUsers(selectedCategoryFilter);
      setDeleteAllDialogOpen(false);
    } catch (err) {
      console.error("Error deleting all users:", err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewTypeChange = () => {
    setViewType(viewType === "card" ? "list" : "card");
  };

  const handleResumeClick = async (path) => {
    console.log(users);
    console.log("tt", path);
    // if (!path) {
    //   alert("Resume is not available");
    //   return;
    // }

    // Ensure the path uses forward slashes
    const normalizedPath = path.replace(/\\/g, "/");
    const fileURL = `${apiUrl}${normalizedPath}`;
    console.log("here is resume: ", fileURL);

    try {
      const response = await fetch(`${apiUrl}ai/search_resume/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resume_url: fileURL }),
      });

      const contentType = response.headers.get("content-type");

      if (response.ok) {
        if (contentType && contentType.includes("application/pdf")) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank");
        } else {
          console.log("Unexpected response from server");
        }
      } else {
        if (contentType && contentType.includes("text/html")) {
          const htmlContent = await response.text();
          const newWindow = window.open();
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          const data = await response.json();
          console.log(data.message);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const renderTable = () => {
    const paginatedData = users.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    console.log('paginatedData dATA',paginatedData)
    return viewType === "card" ? (
      <ApplicationTable
        candidates={paginatedData}
        viewType={viewType}
        setViewType={handleViewTypeChange}
        dataType="scraped_users"
        onDelete={handleDeleteUser}
        hideSearchBar={true}
        hideSwitchButton={true}
        hideCoverLetter={true}
        showSourceInfo={true}
      />
    ) : (
      <ApplicationTable2
        data={paginatedData}
        viewType={viewType}
        setViewType={handleViewTypeChange}
        dataType="scraped_users"
        onDelete={handleDeleteUser}
        hideSearchBar={true}
        hideSwitchButton={true}
        hideCoverLetter={true}
      />
    );
  };

  useEffect(() => {
    fetchCategories();
  }, [recruiterId]);

  // Update the filter handlers
  const handleSourceFilterChange = (event) => {
    setSourceFilter(event.target.value);
  };

  const handleCategoryFilterChange = (event) => {
    const newCategoryId = event.target.value;
    // Notify the parent component to update selectedCategoryFilter
    if (onCategoryFilterChange) {
      onCategoryFilterChange(newCategoryId);
    }
  };

  return (
    <Grow in={true}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0,
          }}
        >
          {/* note_to_translate */}
          {/* <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: 400,
              fontSize: "18px",
              color: colors.primary,
            }}
          >
            {t("dataImportViewer.title")}
          </Typography> */}
          {/* <CancelButton
            sx={{
              color: "#fff",
              "&.Mui-disabled": {
                bgcolor: "#D9D9D933",
                color: "#fff",
                borderColor: "white",
                "& .MuiTypography-root": {
                  color: "#fff",
                },
                "& img": {
                  filter: "brightness(0) saturate(0)",
                  opacity: 0.15,
                  color: "#fff !important",
                },
              },
            }}
            variant="contained"
            color="error"
            onClick={() => setDeleteAllDialogOpen(true)}
            disabled={users.length === 0}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img src={cancel_icon} alt="Cancel" />
              <Typography
                sx={{
                  color: "#fff !important",
                  fontWeight: 500,
                }}
              >
                {t("dataImportViewer.deleteAll")}
              </Typography>
            </Box>
          </CancelButton> */}
          {/* note_to_translate */}
        </Box>

        {/* Filter Controls */}
        {/* <Box sx={{ display: "flex", gap: 2, mb: 3 }}> */}
          {/* <FormControl sx={{ minWidth: 220 }}>
            <StyledTypography sx={{ mb: 0.5 }}>
              {t("dataImportViewer.source")}
            </StyledTypography>
            <StyledSelect
              value={sourceFilter}
              onChange={handleSourceFilterChange}
            >
              <MenuItem value="">{t("dataImportViewer.all")}</MenuItem>
              <MenuItem value="data_imported">Données importées</MenuItem>
              <MenuItem value="email_scraping">Scraping d'e-mails</MenuItem>
            </StyledSelect>
          </FormControl> */}
          {/* <FormControl sx={{ minWidth: 220 }}>
            <StyledTypography sx={{ mb: 0.5 }}>
              {t("dataImportViewer.category")}
            </StyledTypography>
            <StyledSelect
              value={selectedCategoryFilter || ""}
              onChange={handleCategoryFilterChange}
            >
              <MenuItem value="">{t("dataImportViewer.all")}</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name_fr}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl> */}
        {/* </Box> */}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{}}>{renderTable()}</Box>
            {users.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  mb: 2,
                }}
              >
                <CustomPagination
                  page={page}
                  handleChangePage={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={users.length}
                />
              </Box>
            )}
          </>
        )}

        {/* Delete Single User Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>{t("dataImportViewer.confirmDeletion")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("dataImportViewer.confirmDeleteMessage", {
                name: selectedUser?.name,
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              {t("dataImportViewer.cancel")}
            </Button>
            <Button
              color="error"
              onClick={() => handleDeleteUser(selectedUser?.id)}
              variant="contained"
            >
              {t("dataImportViewer.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete All Dialog */}
        <Dialog
          open={deleteAllDialogOpen}
          onClose={() => setDeleteAllDialogOpen(false)}
        >
          <DialogTitle>{t("dataImportViewer.confirmDeleteAll")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("dataImportViewer.confirmDeleteAllMessage")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteAllDialogOpen(false)}>
              {t("dataImportViewer.cancel")}
            </Button>
            <Button color="error" onClick={handleDeleteAll} variant="contained">
              {t("dataImportViewer.deleteAll")}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Grow>
  );
};

export { ScrapedUsersViewer };
